// src/components/subscriptions/SubscriptionOrdersModal.tsx
// Окно «Заказы клиента» в списке абонементов: только просмотр (без управления).
// Показывает заказы владельца абонемента: пригласительный или нет, тур и город,
// места и цены. Кнопка «Перейти к заказам» открывает страницу заказов с фильтром по email.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { getOrdersRequest } from "../../services/api/orders.api";
import { OrderItem, OrderTourItem } from "../../services/types/orders.types";
import { SubscriptionItem } from "../../services/types/subscriptions.types";

// Сколько последних заказов показываем в окне; полный список — на странице заказов.
const PAGE_SIZE = 100;

const money = (value?: string | number | null) => {
    const n = Number(value ?? 0);
    return `${(Number.isNaN(n) ? 0 : n).toFixed(2)} €`;
};

const dateTime = (value?: string | null) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const seatLabel = (item: OrderTourItem) => {
    const parts = [
        item.sector || null,
        item.row ? `ряд ${item.row}` : null,
        item.seat ? `место ${item.seat}` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "Место";
};

// Цена места: итоговая (со скидкой места), иначе цена из прайса.
const seatPrice = (item: OrderTourItem) =>
    item.final_price !== undefined && item.final_price !== null ? item.final_price : item.price;

interface Props {
    subscription: SubscriptionItem;
    onClose: () => void;
}

const SubscriptionOrdersModal: React.FC<Props> = ({ subscription, onClose }) => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState<OrderItem[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const customerId = subscription.customer_id;
    const email = subscription.customer_info?.email || "";
    const customerName = [subscription.customer_info?.first_name, subscription.customer_info?.last_name]
        .filter(Boolean)
        .join(" ");

    useEffect(() => {
        if (!customerId && !email) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        // Точнее всего — по id клиента; email — запасной вариант, если клиента удалили.
        getOrdersRequest(1, PAGE_SIZE, customerId ? { customer_id: customerId, is_deleted: false } : { customer_email: email, is_deleted: false })
            .then((res) => {
                setOrders(res.items || []);
                setTotal(res.total_size ?? (res.items || []).length);
            })
            .catch((e) => setError(e?.message || "Не удалось загрузить заказы"))
            .finally(() => setIsLoading(false));
    }, [customerId, email]);

    const goToOrders = () => {
        onClose();
        navigate(`/orders?customer_email=${encodeURIComponent(email)}`);
    };

    return (
        <Modal
            isOpen
            width="xl"
            title={`Заказы клиента${customerName ? ` — ${customerName}` : ""}`}
            onClose={onClose}
        >
            <div className="sub-orders">
                <div className="sub-orders-head">
                    <span className="sub-orders-muted">
                        Абонемент {subscription.subscription_number}
                        {email ? ` · ${email}` : ""}
                        {!isLoading && !error ? ` · заказов: ${total}` : ""}
                    </span>
                    {email ? (
                        <Button type="button" onClick={goToOrders}>
                            Перейти к заказам
                        </Button>
                    ) : null}
                </div>

                {isLoading ? (
                    <p className="sub-orders-muted">Загрузка заказов…</p>
                ) : error ? (
                    <div className="form-global-error">{error}</div>
                ) : orders.length === 0 ? (
                    <p className="sub-orders-muted">У клиента пока нет заказов.</p>
                ) : (
                    <div className="sub-orders-list">
                        {orders.map((order) => {
                            const isInvite = order.discount_kind === "invite";
                            const subscriptionDiscount = Number(order.subscription_discount_amount ?? 0);
                            return (
                                <div className="sub-order" key={order.id}>
                                    <div className="sub-order-top">
                                        <div className="sub-order-title">
                                            <b>{order.order_number}</b>
                                            <span className="sub-orders-muted">{dateTime(order.created_at)}</span>
                                        </div>
                                        <div className="sub-order-badges">
                                            <span className={`sub-badge ${isInvite ? "sub-badge-invite" : ""}`}>
                                                {isInvite ? "Пригласительный" : "Не пригласительный"}
                                            </span>
                                            <span className={`sub-badge ${order.is_paid ? "sub-badge-paid" : "sub-badge-unpaid"}`}>
                                                {order.is_paid ? "Оплачен" : "Не оплачен"}
                                            </span>
                                            {subscriptionDiscount > 0 ? (
                                                <span className="sub-badge">
                                                    Скидка абонемента {money(subscriptionDiscount)}
                                                </span>
                                            ) : null}
                                            <span className="sub-order-total">{money(order.total_price)}</span>
                                        </div>
                                    </div>

                                    {(order.tours || []).length === 0 ? (
                                        <div className="sub-orders-muted sub-order-tour">Мест в заказе нет.</div>
                                    ) : null}
                                    {(order.tours || []).map((tour, idx) => (
                                        <div className="sub-order-tour" key={`${tour.tour_city_id || idx}`}>
                                            <div className="sub-order-tour-name">
                                                {tour.tour_name_ru || tour.tour_name_de || "Тур"}
                                                <span className="sub-orders-muted">
                                                    {" — "}
                                                    {tour.city_name_ru || tour.city_name_de || "город не указан"}
                                                    {tour.hall_name_ru || tour.hall_name_de
                                                        ? `, ${tour.hall_name_ru || tour.hall_name_de}`
                                                        : ""}
                                                </span>
                                            </div>
                                            <ul className="sub-order-seats">
                                                {(tour.items || []).map((item, i) => (
                                                    <li key={`${item.hall_seat_price_id || i}-${i}`}>
                                                        <span>{seatLabel(item)}</span>
                                                        <span className="sub-order-seat-price">
                                                            {isInvite ? "пригласительный" : money(seatPrice(item))}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                        {total > orders.length ? (
                            <p className="sub-orders-muted">
                                Показаны последние {orders.length} из {total}. Все заказы — по кнопке «Перейти к заказам».
                            </p>
                        ) : null}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default SubscriptionOrdersModal;
